import styled, { keyframes } from "styled-components";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;

  .spin { animation: ${spin} 1s linear infinite; }

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 32px 0 8px 0;
    margin-bottom: 1.5em;

    .header-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${({ theme }) => theme.text};
      font-size: 20px;
      margin-right: 1em;
      opacity: 0.5;
    }

    .header-details {
      h3 {
        margin: 0 0 6px 0;
        font-size: 1.1em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.01em;
        opacity: 0.9;
      }

      .header-meta {
        display: flex;
        gap: 8px;
        font-size: 0.75em;
        flex-wrap: wrap;

        .status-tag {
          padding: 4px 10px;
          border-radius: 4px;
          font-weight: 500;
          background: #10b98115;
          color: #10b981;
          border: 1px solid #10b98130;
        }

        .update-tag {
          padding: 4px 10px;
          border-radius: 4px;
          background: ${({ theme }) => theme.background};
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          border: 1px solid ${({ theme }) => theme.border};
        }
      }
    }
  }

  /* ========== TABS ========== */
  .tabs-header {
    display: flex;
    gap: 10px;
    margin-bottom: 1em;
    flex-wrap: wrap;

    .tab {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 18px;
      background: ${({ theme }) => theme.buttonBg};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 8px;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      font-size: 0.85em;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: inherit;

      &:hover { opacity: 1; border-color: ${({ theme }) => theme.primary}; }

      &.active {
        opacity: 1;
        background: ${({ theme }) => theme.primary};
        border-color: ${({ theme }) => theme.primary};
        color: #fff;
      }
    }
  }

  /* ========== KPI CARDS ========== */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    margin-bottom: 0.8em;

    @media (max-width: 1200px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 600px) { grid-template-columns: 1fr; }
  }

  .kpi-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 8px;
    padding: 20px;
    min-height: 90px;
    transition: all 0.2s ease;

    &:hover { box-shadow: 0 0 15px rgba(0,0,0,0.03); transform: translateY(-0.5px); }

    &.primary { border-left: 4px solid #22c55e; .thumb { color: #22c55e; } }
    &.success { border-left: 4px solid #10b981; .thumb { color: #10b981; } }
    &.warning { border-left: 4px solid #f59e0b; .thumb { color: #f59e0b; } }
    &.info    { border-left: 4px solid #6366f1; .thumb { color: #6366f1; } }

    .card-content {
      display: flex;
      align-items: center;
      gap: 16px;

      .thumb { opacity: 0.7; font-size: 1.1em; flex-shrink: 0; }

      .text {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .kpi-label {
          font-size: 11px;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          text-transform: uppercase;
          letter-spacing: 0.6px;
        }

        .kpi-value {
          font-size: 22px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.03em;
        }
      }
    }
  }

  /* ========== TOOLBAR ========== */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1em;
    margin-bottom: 0.8em;
    padding: 12px 16px;
    background: ${({ theme }) => theme.card2};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 10px;
    flex-wrap: wrap;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;
      min-width: 200px;
    }

    .toolbar-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 6px;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      font-size: 0.8em;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover { opacity: 1; border-color: ${({ theme }) => theme.primary}; color: ${({ theme }) => theme.primary}; }
      &.primary {
        opacity: 1;
        background: ${({ theme }) => theme.primary};
        border-color: ${({ theme }) => theme.primary};
        color: #fff;
      }
    }

    .search-box {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 12px;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 6px;
      flex: 1;
      min-width: 150px;

      svg { color: ${({ theme }) => theme.text}; opacity: 0.5; }
      input {
        background: transparent;
        border: none;
        color: ${({ theme }) => theme.text};
        font-size: 0.85em;
        font-family: inherit;
        outline: none;
        flex: 1;
        &::placeholder { color: ${({ theme }) => theme.text}; opacity: 0.5; }
      }
    }
  }

  /* ========== TABLE ========== */
  .table-wrapper {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    overflow: hidden;
  }

  .table-scroll {
    max-height: 560px;
    overflow-y: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85em;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${({ theme }) => theme.card2};
    color: ${({ theme }) => theme.text};
    opacity: 0.9;
    font-size: 0.72em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    text-align: left;
    padding: 12px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
  }

  tbody td {
    padding: 13px 16px;
    color: ${({ theme }) => theme.text};
    border-bottom: 1px solid ${({ theme }) => theme.border};
    opacity: 0.85;
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.15s ease;
    &:hover { background: ${({ theme }) => theme.card2}; td { opacity: 1; } }
    &:last-child td { border-bottom: none; }
  }

  .cell-main { font-weight: 600; }
  .cell-sub { font-size: 0.8em; opacity: 0.55; }
  .cell-mono { font-family: 'JetBrains Mono', monospace; font-size: 0.82em; }

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72em;
    font-weight: 600;

    &.green  { background: #10b98118; color: #10b981; }
    &.red    { background: #ef444418; color: #ef4444; }
    &.amber  { background: #f59e0b18; color: #d97706; }
    &.blue   { background: #3b82f618; color: #3b82f6; }
    &.gray   { background: rgba(128,128,128,0.12); color: ${({ theme }) => theme.text}; opacity: 0.7; }
  }

  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.border};
    background: transparent;
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover { opacity: 1; border-color: ${({ theme }) => theme.primary}; color: ${({ theme }) => theme.primary}; }
    &.danger:hover { border-color: #ef4444; color: #ef4444; }
  }

  .empty-state {
    padding: 60px 20px;
    text-align: center;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    font-size: 0.9em;

    svg { font-size: 2em; margin-bottom: 12px; }
  }

  /* ========== IMPORT ========== */
  .import-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 22px;

    .hint {
      font-size: 0.8em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      line-height: 1.6;
      margin-bottom: 12px;

      code {
        font-family: 'JetBrains Mono', monospace;
        background: ${({ theme }) => theme.card2};
        padding: 2px 6px;
        border-radius: 4px;
      }
    }

    textarea {
      width: 100%;
      min-height: 180px;
      padding: 12px 14px;
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 8px;
      background: ${({ theme }) => theme.card2};
      color: ${({ theme }) => theme.text};
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.8em;
      resize: vertical;
      outline: none;

      &:focus { border-color: ${({ theme }) => theme.primary}; }
    }

    .import-actions {
      display: flex;
      gap: 10px;
      margin-top: 14px;
      align-items: center;
      flex-wrap: wrap;
    }
  }

  .add-form {
    display: grid;
    grid-template-columns: 1.4fr 2fr 1fr 1.4fr 1.4fr auto;
    gap: 10px;
    margin-bottom: 14px;
    padding: 14px;
    background: ${({ theme }) => theme.card2};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 10px;

    @media (max-width: 1100px) { grid-template-columns: 1fr 1fr; }

    input {
      padding: 9px 12px;
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 7px;
      background: ${({ theme }) => theme.card};
      color: ${({ theme }) => theme.text};
      font-size: 0.82em;
      font-family: inherit;
      outline: none;
      &:focus { border-color: ${({ theme }) => theme.primary}; }
    }
  }

  /* ========== MODAL ========== */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    backdrop-filter: blur(3px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }

  .modal {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 14px;
    width: 100%;
    max-width: 860px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 22px;
    border-bottom: 1px solid ${({ theme }) => theme.border};

    h4 {
      margin: 0;
      font-size: 1em;
      color: ${({ theme }) => theme.text};
    }

    .sub {
      font-size: 0.78em;
      opacity: 0.5;
      color: ${({ theme }) => theme.text};
      margin-top: 3px;
    }
  }

  .modal-body {
    padding: 20px 22px;
    overflow-y: auto;
    flex: 1;
  }

  .detail-section {
    margin-bottom: 22px;

    .ds-title {
      font-size: 0.72em;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: ${({ theme }) => theme.primary};
      margin-bottom: 10px;
      padding-bottom: 6px;
      border-bottom: 1px solid ${({ theme }) => theme.border};
    }
  }

  .qa {
    display: grid;
    grid-template-columns: minmax(220px, 42%) 1fr;
    gap: 8px 16px;
    padding: 7px 0;
    border-bottom: 1px dashed ${({ theme }) => theme.border};
    font-size: 0.84em;

    &:last-child { border-bottom: none; }

    .qq { color: ${({ theme }) => theme.text}; opacity: 0.55; line-height: 1.45; }
    .aa { color: ${({ theme }) => theme.text}; font-weight: 500; line-height: 1.45; word-break: break-word; }
  }

  .signature-box {
    margin-top: 10px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 10px;
    background: #fff;
    padding: 10px;
    display: inline-block;

    img { display: block; max-width: 360px; width: 100%; }
  }

  .meta-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
    font-size: 0.78em;

    .meta-item {
      background: ${({ theme }) => theme.card2};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 8px;
      padding: 10px 12px;

      .k { color: ${({ theme }) => theme.text}; opacity: 0.5; font-size: 0.9em; }
      .v { color: ${({ theme }) => theme.text}; font-weight: 500; margin-top: 3px; word-break: break-all; }
    }
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 22px;
    border-top: 1px solid ${({ theme }) => theme.border};

    button {
      padding: 9px 18px;
      border-radius: 8px;
      border: 1px solid ${({ theme }) => theme.border};
      background: ${({ theme }) => theme.buttonBg};
      color: ${({ theme }) => theme.text};
      font-size: 0.84em;
      font-family: inherit;
      font-weight: 500;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;

      &.danger {
        background: #ef4444;
        border-color: #ef4444;
        color: #fff;
      }
    }
  }
`;
